import { useMemo } from 'react';
import { type Node } from '@wix/ricos';
import { Type, Target } from '@wix/ambassador-blog-v3-post-http/types';
import { isExperimentEnabled, isPaywallNode } from '@wix/communities-blog-client-common';
import { OOI_EXPERIMENTS } from '@app/experiments';
import { useSelector } from '@app/external/common/components/runtime-context';
import { getTopology } from '@app/external/common/store/topology/topology-selectors';
import { type NormalizedPost } from '@app/external/common/types';

type PaywallContent = NormalizedPost['paywallRichContent'];

export const getPaywallContent = (
  paywallContent: PaywallContent,
  {
    isNewMonetizationEnabled,
    subscribeUrl,
  }: { isNewMonetizationEnabled: boolean; subscribeUrl?: string },
): PaywallContent => {
  // Ricos links a paywall LAYOUT's button to `actionHref`. Ambassador's and ricos' `Node` share a
  // wire shape under two nominal types.
  if (
    isNewMonetizationEnabled &&
    paywallContent?.nodes?.some((node) => isPaywallNode(node as unknown as Node))
  ) {
    return paywallContent;
  }

  const buttonNode = paywallContent?.nodes?.[2];

  if (!paywallContent?.nodes || !buttonNode?.buttonData || buttonNode.buttonData.link) {
    return paywallContent;
  }

  // Copied, not patched in place: this is store state, and a late `subscribeUrl` must relink it.
  const nodes = [...paywallContent.nodes];
  nodes[2] = {
    ...buttonNode,
    buttonData: {
      ...buttonNode.buttonData,
      type: Type.LINK,
      link: {
        url: subscribeUrl,
        anchor: undefined,
        target: Target.SELF,
        rel: { nofollow: true, sponsored: false },
      },
    },
  };

  return { ...paywallContent, nodes };
};

export const usePostPaywallContent = (post: NormalizedPost) => {
  const paywallContent = post?.paywallRichContent;

  const isNewMonetizationEnabled = useSelector((state) =>
    isExperimentEnabled(state, OOI_EXPERIMENTS.NEW_BLOG_MONETIZATION_EXPERIENCE),
  );
  const pricingPlansUrl = useSelector((state) => state.pricingPlansUrl);
  const { baseUrl } = useSelector(getTopology);
  const subscribeUrl = useMemo(() => {
    try {
      if (baseUrl && typeof pricingPlansUrl === 'string') {
        return new URL(baseUrl + pricingPlansUrl).href;
      }
    } catch {
      return undefined;
    }
  }, [baseUrl, pricingPlansUrl]);

  const content = useMemo(
    () => getPaywallContent(paywallContent, { isNewMonetizationEnabled, subscribeUrl }),
    [isNewMonetizationEnabled, paywallContent, subscribeUrl],
  );

  return {
    content,
    subscribeUrl: isNewMonetizationEnabled ? subscribeUrl : undefined,
  };
};
